import { atom, useStore } from 'jotai'
import type { CameraView } from '@/math'

import type { Store } from 'jotai/vanilla/store'
import { Document } from './document'
import { uiAtom, voxelSelectionAtomRaw, paletteIndexAtom } from '../uiState'
import { EventTuple } from './types'
import { ReducedState, reduceEvents } from './reducer'
import { AppEventLog } from './appLog'
import type { VoxelMap } from '../voxelTypes'
import { offsetVoxels } from '../voxelMath'
import { type SnapshotMeta } from '@/document/model'
import { occupancyFromSurface, type Occupancy2D } from '@/core/surface'
import { buildPreviewEvents } from '@/viewmodel/preview'
import {
  samePreviewEvents,
  samePreviewInputs,
  selectPreviewInputs,
  type PreviewInputs,
} from '@/viewmodel/previewStability'
import { hoverSuppression } from '@/state/hoverSuppression'
import { incrementRenderCounter } from '@/perf/renderCounters'
import { hoverPreviewModeOverrideAtom } from '@/voxels/hoverPreviewMode'
import { hoverPreviewBudget } from '@/voxels/hoverPreviewBudget'
import { coerceHexColorNumber } from '@/utils/color'

import { timeFunctionMulti } from '@/perf/log'
import { markStrokeInputForEvents } from '@/perf/frameStats'
import { type CameraSnapshot } from '@/core/cameraFit'
import {
  createVoxelMutationJournal,
  type VoxelMutation,
} from '@/events/mutationJournal'
import { isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { diffGrids, expandCellMutations, type ChunkDelta } from '@/storage/grid/gridDiff'

// ============================================================================
// Core Document State
// ============================================================================

// Version ticks for durable log changes (append/undo/redo)
export const logVersionAtom = atom(0)

// Durable mutation batch for the most recent durable base transition (append, undo, redo):
// `previousBase` -> `base`. The grouping gate (viewmodel/grouping.ts) reads it. `chunkDeltas` is set
// when both bases are grids on one palette (plan §2.3); `mutations` is then a lazy, memoized
// per-cell expansion for consumers that need cells rather than chunks.
export type VoxelMutationBatch = {
  sequence: number
  previousBase: VoxelMap
  base: VoxelMap
  readonly mutations: readonly VoxelMutation[]
  chunkDeltas: readonly ChunkDelta[] | null
}

// Shared empty mutation list so "no mutations" keeps a stable identity across recomputes.
const EMPTY_MUTATIONS: readonly VoxelMutation[] = Object.freeze([]) as readonly VoxelMutation[]

let voxelMutationSequence = 0
export const voxelMutationBatchAtom = atom<VoxelMutationBatch | null>(null)

/**
 * Batch for a durable base transition that did not go through the reducer (undo, redo, draft
 * revert): both grids on one palette diff by chunk ref (O(touched chunks), plan §3.3). Returns null
 * when the bases are not comparable grids; consumers then fall back to their full-rebuild paths.
 */
export function createBaseTransitionBatch(previousBase: VoxelMap, base: VoxelMap): VoxelMutationBatch | null {
  if (!isChunkedVoxelGrid(previousBase) || !isChunkedVoxelGrid(base)) return null
  const deltas = diffGrids(previousBase, base)
  if (deltas === 'incomparable') return null
  const palette = base.palette
  let expanded: readonly VoxelMutation[] | null = deltas.length === 0 ? EMPTY_MUTATIONS : null
  return {
    sequence: ++voxelMutationSequence,
    previousBase,
    base,
    chunkDeltas: deltas,
    get mutations() {
      if (expanded === null) expanded = expandCellMutations(deltas, palette)
      return expanded
    },
  }
}


// The actual document instance
const baseDocumentAtom = atom<Document>(Document.create('Untitled'))

// Wrapped document atom that provides read access
export const documentAtom = atom((get) => get(baseDocumentAtom))

// Base map accessor atom (tracks documentVersion for react updates)
// Durable state: reduction of the durable log → { base, ui }
export type DurableSnapshot = { base: VoxelMap; ui: any; meta: SnapshotMeta }

export const durableStateAtom = atom(function durableStateAtomFn(get): DurableSnapshot {
  // Recompute when log pointer changes
  get(logVersionAtom)
  const appLog = get(eventLogAtom)
  const checkpoint = appLog.getLatestCheckpoint()
  if (!checkpoint) console.trace('No checkpoint')
  if (!checkpoint) throw new Error("checkpoint missing!")
  const start: DurableSnapshot = { ...checkpoint } as DurableSnapshot
  // Gather events to replay up to pointer
  const entries = appLog.getEntriesAfter(checkpoint?.index ?? -1)
  const flat: EventTuple[] = []
  for (const entry of entries) {
    if (entry.kind === 'event') flat.push(entry.event)
    else for (const ev of entry.events) flat.push(ev)
  }
  if (flat.length === 0) return start
  return reduceEvents(start, flat)
})


const durableStateProp = <K extends keyof DurableSnapshot>(k: K) =>
  atom((get) => get(durableStateAtom)[k] as DurableSnapshot[K])

const metaAtom = durableStateProp('meta')

const metaProp = <K extends keyof SnapshotMeta>(k: K) =>
  atom((get) => get(metaAtom)[k] as SnapshotMeta[K])

export const baseAtom = durableStateProp('base')
export const boundsAtom = metaProp('bounds')
export const backgroundColorAtom = metaProp('backgroundColor')
export const backgroundAtom = metaProp('background')
export const documentBackdropAtom = metaProp('backdrop')
export const surfaceAtom = metaProp('surface')
export const paletteAtom = metaProp('palette')
export const cameraModeAtom = atom((get) => get(metaAtom).camera.mode)

export const surfaceOccupancyAtom = atom<Occupancy2D | null>((get) => {
  const surface = get(surfaceAtom)
  if (!surface) return null
  return occupancyFromSurface(surface, get(boundsAtom))
})

// ============================================================================
// Event System Integration
// ============================================================================

// Utility to flatten event tuples, expanding any 'transaction' events
function flattenEvents(input: EventTuple | EventTuple[]): EventTuple[] {
  const result: EventTuple[] = []

  const pushEvent = (ev: EventTuple) => {
    if (ev[0] === 'transaction') {
      const [, nested] = ev as any
      for (const e of (nested as EventTuple[])) pushEvent(e)
      return
    }
    result.push(ev)
  }

  if (Array.isArray(input) && Array.isArray((input as any)[0])) {
    for (const ev of input as EventTuple[]) pushEvent(ev)
  } else {
    pushEvent(input as EventTuple)
  }

  return result
}

// Unified batch processor: flatten, stage UI, and conditionally clone base
type BatchOpts = { log?: boolean; extendLastStep?: boolean }

// Checkpoint policy: checkpoint on every durable append; AppEventLog keeps a sliding window.
// With `extendLastStep`, the durable events join the step at the pointer instead of starting a new
// one (AppEventLog.extendLastStep); returns false, changing nothing, when the log refuses.
function processEventsBatch(get: any, set: any, input: EventTuple | EventTuple[], opts: BatchOpts = {}): boolean {
  // Normalize input to a flat array of events
  const events: EventTuple[] = flattenEvents(input)
  markStrokeInputForEvents(events) // frame-stats probe: input-to-present timing (no-op unless installed)

  // Collect loggable events for durable history
  const durableEvents: EventTuple[] = []
  for (const ev of events) if (isDurableEvent(ev)) durableEvents.push(ev)

  const appLog = get(eventLogAtom) as AppEventLog
  if (opts.extendLastStep && (durableEvents.length === 0 || !appLog.canExtendLastStep())) return false

  const { base, meta } = get(durableStateAtom)
  let init = { ui: get(uiAtom), base, meta } as ReducedState


  // Reduce once over durable base + current UI to compute new { base, ui }
  const mutationJournal = durableEvents.length > 0 ? createVoxelMutationJournal() : undefined
  // Inside an open draft (a stroke being drawn), handlers can read the document as it was before it.
  const strokeBase = appLog.isDraftActive() ? appLog.getDraftBaseCheckpoint()?.base : undefined
  const reduced = timeFunctionMulti('[state] reduceEvents(batch)', () =>
    reduceEvents(init, events, mutationJournal || strokeBase ? { mutationJournal, strokeBase } : undefined)
  )

  // Update live UI with staged changes (durable base is derived from log/checkpoints)
  set(uiAtom, reduced.ui)

  // Write to app log
  if (opts.log !== false) {
    if (opts.extendLastStep) appLog.extendLastStep(durableEvents)
    else if (durableEvents.length === 1) appLog.appendEvent(durableEvents[0])
    else if (durableEvents.length > 1) appLog.appendTransaction(durableEvents, `Batch: ${durableEvents.length} operations`)
  }

  if (opts.log !== false && !opts.extendLastStep) coalescePaletteEdit(appLog, durableEvents)

  // Only checkpoint when we actually appended durable events
  if (durableEvents.length > 0) {
    // Create a checkpoint for the current pointer using the reduced state
    appLog.checkpoint({ base: (reduced as any).base, ui: {}, meta: (reduced as any).meta })
    const journal = mutationJournal
    set(voxelMutationBatchAtom, {
      sequence: ++voxelMutationSequence,
      previousBase: base,
      base: reduced.base,
      chunkDeltas: journal ? journal.chunkDeltas : null,
      // Lazy: in chunk mode the journal expands per-cell mutations only when a consumer reads them.
      get mutations() { return journal ? journal.mutations : EMPTY_MUTATIONS },
    } satisfies VoxelMutationBatch)
  }

  // Bump log version only for durable history changes
  if (durableEvents.length > 0) set(logVersionAtom, (v: number) => v + 1)
  return true
}

/*
 * One undo step per palette gesture. Palette edits are undo boundaries (appLog.ts), and a colour drag
 * or a density scrub sends one per move. A palette edit that follows the previous one on the same
 * item and kind, with nothing appended in between, replaces it in the log (the later edit carries the
 * whole new value, so the state is the same), so the gesture undoes in one step: while a gesture is
 * open (`beginPaletteGesture()`, a picker's press, until `endPaletteGesture()`, its release), or
 * else within `PALETTE_GESTURE_MS` of the previous edit (a scrub with no release of its own).
 */
const PALETTE_GESTURE_MS = 800
let lastPaletteEdit: { log: AppEventLog; key: string; index: number; at: number } | null = null
let paletteGestureOpen = false

function paletteEditKey(event: EventTuple): string | null {
  const type = event[0]
  if (type === 'palette.setItems') return type
  if (type === 'palette.setItem' || type === 'palette.setItemColor') return `${type}:${String(event[1])}`
  return null
}

function coalescePaletteEdit(appLog: AppEventLog, durableEvents: readonly EventTuple[]): void {
  const key = durableEvents.length === 1 ? paletteEditKey(durableEvents[0]) : null
  if (!key || appLog.isDraftActive()) {
    if (durableEvents.length > 0) {
      lastPaletteEdit = null
      paletteGestureOpen = false
    }
    return
  }
  const now = performance.now()
  const head = appLog.getPointer()
  const prev = lastPaletteEdit
  if (prev && prev.log === appLog && prev.key === key && (paletteGestureOpen || now - prev.at < PALETTE_GESTURE_MS) && prev.index === head - 1) {
    appLog.coalesceRangeWith(prev.index, head + 1, (_a, b) => b)
    lastPaletteEdit = { log: appLog, key, index: prev.index, at: now }
    return
  }
  lastPaletteEdit = { log: appLog, key, index: head, at: now }
}

/** Opens a palette gesture: its edits fold into one undo step until `endPaletteGesture()`. */
export function beginPaletteGesture(): void {
  if (!paletteGestureOpen) lastPaletteEdit = null
  paletteGestureOpen = true
}

/** Ends the open palette gesture: the next palette edit is a new undo step. */
export function endPaletteGesture(): void {
  paletteGestureOpen = false
  lastPaletteEdit = null
}

// Single dispatcher for ALL mutations - this is the ONLY way to change state
// Called before undo/redo moves the log pointer, so a world with an open draft (a flow that is still
// settling, src/experiences) can commit it first and the undo applies to that whole step.
const beforeHistoryHooks = new Set<() => void>()
export function registerBeforeHistoryHook(hook: () => void): () => void {
  beforeHistoryHooks.add(hook)
  return () => { beforeHistoryHooks.delete(hook) }
}

export const dispatchAtom = atom(
  null,
  function dispatchAtomFn(get, set, eventTuple: EventTuple) {
    const type = eventTuple[0]
    const appLog = get(eventLogAtom)

    if (type === 'undo' || type === 'redo') {
      lastPaletteEdit = null
      paletteGestureOpen = false
      for (const hook of beforeHistoryHooks) hook()
      const previousBase = get(durableStateAtom).base
      const ok = type === 'undo' ? appLog.undo() : appLog.redo()
      if (!ok) {
        return
      }
      // Pointer moved: bump version; preview/composed state derives from durable. Publish the
      // transition as a real batch (chunk diff between the two checkpointed grids) so the grouping
      // gate and the indexed solid runtime take their incremental branches on undo/redo.
      set(logVersionAtom, (v: number) => v + 1)
      set(voxelMutationBatchAtom, createBaseTransitionBatch(previousBase, get(durableStateAtom).base))
      return
    }

    processEventsBatch(get, set, eventTuple)
  }
)

/**
 * Reverts the active draft (see AppEventLog.revertDraft) and publishes the transition batch from
 * the draft head back to the pinned pre-draft base, like undo.
 */
export const revertDraftAtom = atom(null, function revertDraftAtomFn(get, set) {
  const appLog = get(eventLogAtom)
  if (!appLog.isDraftActive()) return
  const previousBase = get(durableStateAtom).base
  appLog.revertDraft()
  set(logVersionAtom, (v: number) => v + 1)
  set(voxelMutationBatchAtom, createBaseTransitionBatch(previousBase, get(durableStateAtom).base))
})

// Batch dispatcher atom - processes events in a transaction
const dispatchManyAtom = atom(
  null,
  function dispatchManyAtomFn(get, set, events: EventTuple[]) {
    processEventsBatch(get, set, events)
  }
)

const extendLastStepAtom = atom(null, function extendLastStepAtomFn(get, set, events: EventTuple[]): boolean {
  return processEventsBatch(get, set, events, { extendLastStep: true })
})

/**
 * Consequences of a step: applies `events` and folds them into the step that just committed (see
 * AppEventLog.extendLastStep), so undo removes the user's step and its consequences together and
 * redo restores both. Call it in reaction to a user's step (a stroke's commit: `strokeActiveAtom`
 * falling with a new head commit id), never to undo/redo. Returns false, changing nothing, when
 * there is no step to extend (draft open, redo tail, empty log) or no durable events.
 */
export function extendLastStep(store: Store, events: EventTuple[]): boolean {
  if (events.length === 0) return false
  return store.set(extendLastStepAtom, events)
}

export const dispatch = (store: Store, eventOrEvents: EventTuple | EventTuple[]) => {
  if (!eventOrEvents || eventOrEvents.length === 0) {
    return
  }
  // Check if we have a batch by looking at the first element
  const isBatch = Array.isArray(eventOrEvents[0])

  if (isBatch) {
    // Batch dispatch
    store.set(dispatchManyAtom, eventOrEvents as EventTuple[])
  } else {
    // Single event
    store.set(dispatchAtom, eventOrEvents as EventTuple)
  }
}

/** Undo the last step (an open page draft commits first, `registerBeforeHistoryHook`). */
export const undo = (store: Store) => dispatch(store, ['undo'])
/** Redo the next step. */
export const redo = (store: Store) => dispatch(store, ['redo'])

// Dispatch function types
export type StoreDispatcher = (store: Store, eventOrEvents: EventTuple | EventTuple[]) => void
export type Dispatcher = (eventOrEvents: EventTuple | EventTuple[]) => void

export const useDispatch = (): Dispatcher => {
  const store = useStore()
  return (eventOrEvents: EventTuple | EventTuple[]) => dispatch(store, eventOrEvents)
}

// ============================================================================
// Computed State (Derived from Document)
// ============================================================================

// Derived preview layers from UI (selection, shape, rectangle)
// Current selection voxels (transformed), derived from base only
export const selectionVoxelsAtom = atom(function selectionVoxelsAtomFn(get) {
  const base = get(baseAtom) as VoxelMap
  const bounds = get(boundsAtom)
  const sel = get(voxelSelectionAtomRaw)
  if (!sel) return new Map() as VoxelMap
  return offsetVoxels(sel.keys || [], sel.translate, bounds, base)
})


// Preview stability (A1, plan §2.2): the preview reads a narrow selection of the UI state, and the
// selection keeps its previous identity while the hovered cell, tool settings and gesture state are
// unchanged (so the exact hit point moving inside one cell changes nothing downstream). The last
// selection is module state rather than store state: it is only ever reused on value equality, so
// sharing it across stores is harmless.
let lastPreviewInputs: PreviewInputs | null = null
let lastPreviewEvents: EventTuple[] | null = null

export const previewInputsAtom = atom((get): PreviewInputs => {
  const next = selectPreviewInputs(get(uiAtom), hoverSuppression.isSuppressed())
  const prev = lastPreviewInputs
  if (prev !== null && samePreviewInputs(prev, next)) {
    incrementRenderCounter('previewStableHits')
    return prev
  }
  lastPreviewInputs = next
  return next
})

// Preview events for the current UI state (hover, stroke, line, selection moves, ...). A result equal by value to the previous one is returned by identity, so
// jotai's Object.is check keeps composedStateAtom (and everything downstream) unchanged.
export const previewEventsAtom = atom((get) => {
  const inputs = get(previewInputsAtom)
  return timeFunctionMulti('[state] previewEvents', () => {
    let realHover: { key: string; radius: number } | null = null
    const events = buildPreviewEvents({
      ui: inputs.ui,
      voxelData: get(currentMetaPaletteItemAtom),
      base: get(baseAtom) as VoxelMap,
      bounds: get(boundsAtom),
      camera: get(cameraAtom),
      hoverOverride: get(hoverPreviewModeOverrideAtom),
      onRealHover: (key, radius) => { realHover = { key, radius } },
    })
    const prev = lastPreviewEvents
    if (prev !== null && samePreviewEvents(prev, events)) {
      incrementRenderCounter('previewStableHits')
      return prev
    }
    lastPreviewEvents = events
    // A new real-voxel hover preview: its composition and next flush are measured (hoverPreviewBudget).
    const hover = realHover as { key: string; radius: number } | null
    if (hover) hoverPreviewBudget.noteHoverPreviewIssued(hover.key, hover.radius)
    return events
  })
})


// Extra preview events owned by a single-purpose world (src/experiences), for example hiding the
// half of the paper that is folding over. Applied after the tool's preview events. The empty array
// is a shared constant so worlds that never preview keep every downstream identity stable.
export const NO_EXPERIENCE_PREVIEW: readonly EventTuple[] = Object.freeze([]) as readonly EventTuple[]
export const experiencePreviewEventsAtom = atom<readonly EventTuple[]>(NO_EXPERIENCE_PREVIEW)

// Cells a page motion has lifted out of the document while it draws them elsewhere (a creature's
// hop, src/play/cellHops.ts): a second page channel, applied last, so a motion and a stamp's hover
// preview (which owns `experiencePreviewEventsAtom`) can both preview at once. Same empty constant.
export const motionPreviewEventsAtom = atom<readonly EventTuple[]>(NO_EXPERIENCE_PREVIEW)

let lastCombinedPreview: { tool: EventTuple[]; extra: readonly EventTuple[]; motion: readonly EventTuple[]; combined: EventTuple[] } | null = null
const allPreviewEventsAtom = atom((get): EventTuple[] => {
  const tool = get(previewEventsAtom)
  const extra = get(experiencePreviewEventsAtom)
  const motion = get(motionPreviewEventsAtom)
  if (extra.length === 0 && motion.length === 0) return tool
  const last = lastCombinedPreview
  if (last && last.tool === tool && last.extra === extra && last.motion === motion) return last.combined
  const combined = [...tool, ...extra, ...motion]
  lastCombinedPreview = { tool, extra, motion, combined }
  return combined
})

// Composed state: apply preview events to durable { base, ui }. Clone base only if any non-UI events
// occur. No journal: renderers consume the composed grid itself (the chunked solid runtime diffs
// grids by chunk ref), so a preview reduction records nothing per cell.
export const composedStateAtom = atom<ReducedState>((get) => {
  const durable = get(durableStateAtom)
  const events = get(allPreviewEventsAtom)
  if (events.length === 0) return durable
  const t0 = performance.now()
  const composed = reduceEvents(durable, events)
  hoverPreviewBudget.noteHoverCompose(performance.now() - t0)
  return composed
})
export const composedBaseAtom = atom((get) => get(composedStateAtom).base)
export const composedUiAtom = atom((get) => get(composedStateAtom).ui)

// Can undo/redo
export const canUndoAtom = atom((get) => {
  get(logVersionAtom)
  return get(eventLogAtom).canUndo()
})

export const canRedoAtom = atom((get) => {
  get(logVersionAtom)
  return get(eventLogAtom).canRedo()
})

export const historyAtom = atom((get) => {
  get(logVersionAtom)
  return get(eventLogAtom).historySummary()
})

export const eventLogAtom = atom(new AppEventLog())

// ============================================================================
// Meta-derived atoms (palette from durable meta)
// ============================================================================


const normalizeItem = (it: any) => {
  if (!it) return it
  const t = it.type || 'solid'
  let color = it.color
  if (typeof color === 'string') {
    color = coerceHexColorNumber(color) ?? color
  }
  // Preserve any additional fields (e.g., preset/pattern). Only coerce type/color.
  const rest: any = { ...it }
  delete rest.type
  delete rest.color
  return color != null ? { type: t, color, ...rest } : { type: t, ...rest }
}

export const normalizedPaletteAtom = atom((get) => {
  // const durable = get(durableStateAtom)
  const palette = get(paletteAtom)
  // Normalize palette from meta: coerce string colors to numbers
  return Array.isArray(palette) ? palette.map(normalizeItem) : []
})

export const currentMetaPaletteItemAtom = atom((get) => get(normalizedPaletteAtom)[get(paletteIndexAtom)])
export const recentColorsAtom = metaProp('recentColors')
/** The document-wide blob density override (`SnapshotMeta.blobDensity`); undefined: off. */
export const blobDensityOverrideAtom = metaProp('blobDensity')

// ============================================================================
// Camera View Atom (React-trigger only — not part of durable UI)
// ============================================================================


export const cameraAtom = atom<CameraView | null>(null)

export const cameraViewAtom = atom<CameraSnapshot>({
  position: { x: 0, y: 0, z: 0 },
  quaternion: { x: 0, y: 0, z: 0, w: 1 },
  fov: 50,
  near: 0.1,
  far: 1000,
  target: { x: 0, y: 0, z: 0 },
  mode: 'tilt',
  up: { x: 0, y: 1, z: 0 }
})

export const directionalAtom = atom((get) => get(metaAtom).lighting?.directional)


function isDurableEvent(event: EventTuple): boolean {
  const type = event[0]
  if (!type.startsWith('ui.')) return true
  return false
}
